import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaPaperPlane, FaWhatsapp, FaGlobe } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_URL from '../config/api';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await axios.post(`/contact`, formData);
      if (response.data.success) {
        setSuccess(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-gradient-to-r from-maroon-900 to-maroon-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">{t('contact.hero.title')}</h1>
            <p className="text-xl text-gray-100">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-heading font-bold text-gray-900 mb-6">
                {t('contact.getInTouch')}
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                {t('contact.description')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-maroon-700 rounded-full p-3 mr-4 flex-shrink-0">
                    <FaMapMarkerAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.officeAddress')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-gray-800">PT. ARTHA FINTRUST CONSULTASEAN</strong><br />
                      {i18n.language === 'id' ? (
                        <>
                          18 Office Park<br />
                          Jl. TB Simatupang No. 18<br />
                          Lt. 21 Unit C, Kebagusan<br />
                          Ps. Minggu, Jakarta Selatan<br />
                          DKI Jakarta 12520<br />
                          Indonesia
                        </>
                      ) : (
                        <>
                          18 Office Park<br />
                          #18 TB Simatupang St.<br />
                          21<sup>st</sup> Fl. Unit C, Kebagusan<br />
                          Ps. Minggu, South Jakarta<br />
                          DKI Jakarta 12520 Indonesia
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-maroon-700 rounded-full p-3 mr-4 flex-shrink-0">
                    <FaPhone className="text-white text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.phone')}</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.office')}</p>
                        <a href="tel:+622131118055" className="text-gray-700 hover:text-maroon-700 font-medium transition-colors">
                          (+62) 21-31118055
                        </a>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                          <FaWhatsapp className="text-green-600" /> WhatsApp
                        </p>
                        <a href="https://wa.me/447423369031" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-green-600 font-medium transition-colors">
                          +44-7423369031
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-maroon-700 rounded-full p-3 mr-4 flex-shrink-0">
                    <FaEnvelope className="text-white text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.emailWebsite')}</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.email')}</p>
                        <a href="mailto:info@aftrustca.co.id" className="text-gray-700 hover:text-maroon-700 font-medium transition-colors break-all">
                          info@aftrustca.co.id
                        </a>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                          <FaGlobe /> {t('contact.website')}
                        </p>
                        <a href="https://www.aftrustca.co.id" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-maroon-700 font-medium transition-colors">
                          www.aftrustca.co.id
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-gradient-to-br from-maroon-50 to-gold-50 rounded-xl border-2 border-gold-200">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    {t('contact.businessHours')}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>{t('contact.monday')}</span>
                      <span className="font-semibold text-gray-800">{t('contact.businessTime')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('contact.saturday')}</span>
                      <span className="font-semibold text-gray-800">{t('contact.byAppointment')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('contact.sunday')}</span>
                      <span className="font-semibold text-red-600">{t('contact.closed')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-heading font-bold text-gray-900 mb-6">
                {t('contact.sendMessage')}
              </h2>

              {success && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  {t('contact.successMessage')}
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {t('contact.errorMessage')}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.fullName')} {t('contact.required')}
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent transition-colors" placeholder={t('contact.namePlaceholder')} />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.emailAddress')} {t('contact.required')}
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent transition-colors" placeholder={t('contact.emailPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.phoneNumber')}
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent transition-colors" placeholder={t('contact.phonePlaceholder')} />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.subject')} {t('contact.required')}
                  </label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent transition-colors" placeholder={t('contact.subjectPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.message')} {t('contact.required')}
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent transition-colors resize-none" placeholder={t('contact.messagePlaceholder')}></textarea>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-maroon-700 to-maroon-900 text-white font-bold py-4 px-6 rounded-lg hover:from-maroon-800 hover:to-black-900 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed group">
                  {loading ? t('contact.sending') : (<><span>{t('contact.sendButton')}</span><FaPaperPlane className="ml-2 group-hover:translate-x-1 transition-transform" /></>)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-heading font-bold text-gray-900 mb-6 text-center">
              {t('contact.findUs')}
            </h2>
            <div className="w-full h-96 bg-gray-200 rounded-lg overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.5289157817755!2d106.8216829!3d-6.3184806!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69ed84a63fc30f%3A0xe84c2a9b5b0c7b7b!2sJl.%20TB%20Simatupang%20No.18%2C%20Kebagusan%2C%20Ps.%20Minggu%2C%20Jakarta%20Selatan%2C%20Daerah%20Khusus%20Ibukota%20Jakarta%2012520!5e0!3m2!1sen!2sid!4v1729753200000!5m2!1sen!2sid"
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="AFTRUSTCA Office Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
